import {
  Box,
  Button,
  IconButton,
  Input,
  InputAdornment,
  Typography,
  Avatar,
  Stack,
  Paper,
  Fade,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { capitalize, debounce } from "lodash";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import {
  LeagueManagementService,
  playerServices,
  teamManagementApi,
} from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import TableDropDown from "../../components/shared/TableDropDown";
import { useSearchParams } from "react-router-dom";
import { ADMIN_ROLES } from "../../utils/constants";
import { Loader } from "../../components/CustomLoader";
import { Helmet } from "react-helmet-async";
import {
  updateSearchParams,
  updatePageParams,
  updateFilterParams,
} from "../../utils/urlParams";
import "./UserManagement.css";
import { SportsHandball } from "@mui/icons-material";
import TeamPositionsModal from "./TeamPositionsModal";

const positionMap = {
  24: "goalkeeper",
  25: "defender",
  26: "midfielder",
  27: "forward",
};

const getLeagueId = (league) =>
  league ? String(league.leagueId ?? league._id ?? "") : "";

const getTeamId = (team) => (team ? String(team.teamId ?? team._id ?? "") : "");

const resolveLeagueIdFromLeagueData = (leagueData) => {
  if (!leagueData) return "";
  return String(leagueData.leagueId ?? leagueData._id ?? leagueData.id ?? "");
};

const resolveTeamCountryId = (leagueData, countries) => {
  if (!leagueData) return "";
  if (leagueData.countryId != null && leagueData.countryId !== "") {
    return String(leagueData.countryId);
  }
  if (leagueData.country && countries?.length) {
    const match = countries.find(
      (c) =>
        c.country?.toLowerCase() === String(leagueData.country).toLowerCase(),
    );
    return match ? String(match.countryId) : "";
  }
  return "";
};

const isTeamCompatibleWithFilters = (
  teamObj,
  countryId,
  leagueId,
  countries,
) => {
  const hasParentFilter = Boolean(countryId || leagueId);
  if (!hasParentFilter) return true;

  const ld = teamObj?.leagueData;
  if (!ld) return false;

  if (leagueId) {
    const teamLeagueId = resolveLeagueIdFromLeagueData(ld);
    if (!teamLeagueId || teamLeagueId !== String(leagueId)) return false;
  }

  if (countryId) {
    const teamCountryId = resolveTeamCountryId(ld, countries);
    if (!teamCountryId || String(teamCountryId) !== String(countryId)) {
      return false;
    }
  }

  return true;
};

const buildTeamSearchBody = (query, countryId, leagueId) => ({
  pageNumber: 1,
  pageSize: 50,
  searchItem: query,
  leagueId: leagueId ? [leagueId] : [],
  countryId: countryId ? [countryId] : [],
});

const buildPlayerSearchBody = (
  query,
  { pageSize, pageNumber, position, team, country, league },
) => ({
  pageSize,
  pageNumber,
  search: query || "",
  positionId: position ? String(position) : "",
  teamId: team ? String(team) : "",
  countryId: country ? String(country) : "",
  leagueId: league ? String(league) : "",
});

export default function PlayerManagementList() {
  const { user: currentUser } = useSelector((state) => state.auth);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [country, setCountry] = useState([]);
  const [leagues, setLeagues] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedLeague, setSelectedLeague] = useState("");

  const [selectedPosition, setSelectedPosition] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");

  const [teamOptions, setTeamOptions] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [selectedTeamObj, setSelectedTeamObj] = useState(null);

  const [allUsers, setAllUsers] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const [editedRows, setEditedRows] = useState({});
  const [editingCell, setEditingCell] = useState({ id: null, field: null });

  const [teamPositionsPlayerId, setTeamPositionsPlayerId] = useState("");
  const [teamPositionsOpen, setTeamPositionsOpen] = useState(false);

  const fetchAllUsersForStats = async () => {
    try {
      const body = {
        pageSize: 1000,
        pageNumber: 1,
        search: "",
        positionId: "",
        teamId: "",
        countryId: "",
      };
      const res = await playerServices.getAllPlayers(body);
      if (res.status === 200 && res?.data?.data?.result) {
        const allUsersData = res.data.data.result;
        setAllUsers(allUsersData);
      }
    } catch (err) {
      console.error("Error fetching all users for stats:", err);
    }
  };

  const fetchPlayers = async (query) => {
    const body = buildPlayerSearchBody(query, {
      pageSize,
      pageNumber: currentPage,
      position: selectedPosition,
      team: selectedTeam,
      country: selectedCountry,
      league: selectedLeague,
    });

    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await playerServices.getAllPlayers(body);
      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.data?.result);
          setTotalPages(res?.data?.data?.totalRecords);
        }
        setLoaderDelay(true);
      }, 500);
    } catch (err) {
      setData(err?.response?.data?.message?.data);
      errorHandler(err);
      setLoaderDelay(false);
    } finally {
      setIsLoading(false);
      setLoaderDelay(false);
    }
  };

  const updatePlayers = async (data) => {
    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await playerServices.updatePlayer(data);
      if (res.status === 200) {
        toast.success(res?.data?.message);
        setEditedRows({});
        fetchPlayers();
      }
    } catch (err) {
      errorHandler(err);
      setLoaderDelay(false);
    } finally {
      setIsLoading(false);
      setLoaderDelay(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      updateSearchParams(searchParams, setSearchParams, query, 1);
      fetchPlayers(query);
    }, 400),
    [
      currentPage,
      pageSize,
      status,
      searchParams,
      setSearchParams,
      selectedPosition,
      selectedCountry,
      selectedTeam,
      selectedLeague,
    ],
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    setCurrentPage(1);
    debouncedSearch(query);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updatePageParams(searchParams, setSearchParams, page);
  };

  const handleCountryChange = (newCountry) => {
    setSelectedCountry(newCountry);
    setCurrentPage(1);
    updateFilterParams(
      searchParams,
      setSearchParams,
      "country",
      newCountry ? [newCountry] : [],
      1,
    );
  };

  const handleCountryAutocompleteChange = (event, newValue) => {
    handleCountryChange(newValue ? newValue.value : "");
  };

  const handleLeagueChange = (newLeague) => {
    setCurrentPage(1);

    setSelectedLeague(newLeague);
    updateFilterParams(
      searchParams,
      setSearchParams,
      "league",
      newLeague ? [newLeague] : [],
      1,
    );
  };

  const handleLeagueAutocompleteChange = (event, newValue) => {
    handleLeagueChange(newValue ? newValue.value : "");
  };

  const handlePositionChange = (newVal) => {
    setSelectedPosition(newVal);
    setCurrentPage(1);
    updateFilterParams(
      searchParams,
      setSearchParams,
      "position",
      newVal !== "" ? [newVal] : [],
      1,
    );
  };

  const handleTeamChange = (event, newValue) => {
    setSelectedTeamObj(newValue);
    const teamIdValue = newValue ? newValue.value : "";
    setSelectedTeam(teamIdValue);
    setCurrentPage(1);
    updateFilterParams(
      searchParams,
      setSearchParams,
      "team",
      teamIdValue ? [teamIdValue] : [],
      1,
    );
  };

  const fetchCountry = async () => {
    try {
      const response = await LeagueManagementService.getCountry();
      if (response.status == 200) {
        setCountry(response?.data?.data || []);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Error in fetching Country.",
      );
    }
  };
  const fetchLeagues = useCallback(async () => {
    try {
      const payload = { countryId: [] };
      const response = await LeagueManagementService.getBids(payload);
      if (response.status === 200) {
        setLeagues(response?.data?.data?.leagues || []);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Error in fetching leagues.",
      );
    }
  }, []);

  const fetchTeams = useCallback(
    async (
      query = "",
      countryId = selectedCountry,
      leagueId = selectedLeague,
    ) => {
      setTeamLoading(true);
      try {
        const body = buildTeamSearchBody(query, countryId, leagueId);
        const response = await teamManagementApi.getPlayer(body);
        if (response.status === 200) {
          const teamData =
            response?.data?.data?.result || response?.data?.data || [];

          const mappedTeams = teamData
            .map((t) => ({
              value: getTeamId(t),
              label: t.teamName || t.name || "Unknown Team",
              leagueData: t.leagueData,
            }))
            .filter((t) =>
              isTeamCompatibleWithFilters(t, countryId, leagueId, country),
            );

          setTeamOptions(mappedTeams);
        }
      } catch (error) {
        console.error("Error fetching teams:", error);
      } finally {
        setTeamLoading(false);
      }
    },
    [selectedCountry, selectedLeague, country],
  );

  const debouncedTeamSearch = useCallback(
    debounce((query, countryId, leagueId) => {
      fetchTeams(query, countryId, leagueId);
    }, 400),
    [fetchTeams],
  );

  useEffect(() => {
    const pageFromUrl = searchParams.get("page");
    const searchFromUrl = searchParams.get("search");
    const countryFromUrl = searchParams.get("country");
    const leagueFromUrl = searchParams.get("league");
    const statusFromUrl = searchParams.get("status");
    const positionFromUrl = searchParams.get("position");
    const teamFromUrl = searchParams.get("team");
    const pageSizeFromUrl = searchParams.get("pageSize");

    if (pageFromUrl && !isNaN(pageFromUrl) && parseInt(pageFromUrl) > 0) {
      setCurrentPage(parseInt(pageFromUrl));
    }

    if (searchFromUrl) {
      setSearchItem(searchFromUrl);
    }

    if (countryFromUrl) {
      setSelectedCountry(countryFromUrl);
    }

    if (leagueFromUrl) {
      setSelectedLeague(leagueFromUrl);
    }

    if (positionFromUrl) {
      setSelectedPosition(parseInt(positionFromUrl) || "");
    }

    if (teamFromUrl) {
      setSelectedTeam(teamFromUrl);
    }

    if (statusFromUrl) {
      const statusArray = statusFromUrl
        .split(",")
        .map((s) => parseInt(s))
        .filter((s) => !isNaN(s));
      setStatus(statusArray);
    }

    if (
      pageSizeFromUrl &&
      !isNaN(pageSizeFromUrl) &&
      parseInt(pageSizeFromUrl) > 0
    ) {
      setPageSize(parseInt(pageSizeFromUrl));
    }

    setTimeout(() => {
      setIsInitialized(true);
    }, 50);
  }, [searchParams]);

  useEffect(() => {
    fetchCountry();
    fetchAllUsersForStats();
  }, []);

  useEffect(() => {
    fetchLeagues();
  }, [fetchLeagues]);

  useEffect(() => {
    setTeamOptions([]);
  }, [selectedCountry, selectedLeague]);

  useEffect(() => {
    fetchTeams("");
  }, [fetchTeams]);

  useEffect(() => {
    if (selectedTeam && !selectedTeamObj) {
      const match = teamOptions.find(
        (t) => String(t.value) === String(selectedTeam),
      );
      if (match) setSelectedTeamObj(match);
    }
  }, [teamOptions, selectedTeam, selectedTeamObj]);

  useEffect(() => {
    if (!isInitialized) return;
    fetchPlayers(searchItem);
  }, [
    isInitialized,
    currentPage,
    pageSize,
    status,
    refresh,
    selectedCountry,
    selectedPosition,
    selectedTeam,
    selectedLeague,
  ]);

  const handleOpenTeamPositions = (playerId) => {
    setTeamPositionsPlayerId(playerId);
    setTeamPositionsOpen(true);
  };

  const handleCloseTeamPositions = () => {
    setTeamPositionsOpen(false);
    setTeamPositionsPlayerId("");
  };

  const handleCellClick = (id, field) => {
    setEditingCell({ id, field });
  };

  const handleCellChange = (id, field, value) => {
    setEditedRows((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveAll = () => {
    const changesToSave = Object.keys(editedRows).map((id) => ({
      _id: id,
      ...editedRows[id],
    }));

    if (changesToSave.length === 0) {
      toast.info("No changes to save.");
      return;
    }
    updatePlayers(changesToSave);
  };

  const handleReset = () => {
    setSearchItem("");
    setSelectedCountry("");
    setSelectedLeague("");
    setSelectedTeam("");
    setSelectedTeamObj(null);
    setStatus([]);
    setSelectedPosition("");
    setCurrentPage(1);
    setPageSize(10);
    setTeamOptions([]);
    setEditedRows({});
    setEditingCell({ id: null, field: null });

    const newSearchParams = new URLSearchParams();
    newSearchParams.set("page", "1");
    setSearchParams(newSearchParams, { replace: true });
  };

  const hasUnsavedEdits = Object.keys(editedRows).length > 0;
  const hasActiveFilters =
    Boolean(searchItem) ||
    Boolean(selectedCountry) ||
    Boolean(selectedLeague) ||
    Boolean(selectedTeam) ||
    Boolean(selectedPosition) ||
    status.length > 0 ||
    currentPage !== 1 ||
    pageSize !== 10;
  const canReset = hasUnsavedEdits || hasActiveFilters;

  const countryOptions = useMemo(
    () =>
      country.map((c) => ({
        value: String(c?.countryId),
        label: c?.country || "",
      })),
    [country],
  );

  const leagueOptions = useMemo(
    () => leagues.map((l) => ({ value: getLeagueId(l), label: l?.name || "" })),
    [leagues],
  );

  const renderEditableCell = (row, field, type = "text") => {
    const isEditing = editingCell.id === row._id && editingCell.field === field;
    const displayValue =
      editedRows[row._id]?.[field] !== undefined
        ? editedRows[row._id][field]
        : row[field];

    if (isEditing) {
      return (
        <Input
          autoFocus
          type={type}
          fullWidth
          value={displayValue || ""}
          onChange={(e) => handleCellChange(row._id, field, e.target.value)}
          onBlur={() => setEditingCell({ id: null, field: null })}
          onKeyDown={(e) =>
            e.key === "Enter" && setEditingCell({ id: null, field: null })
          }
          sx={{ fontSize: "0.875rem" }}
        />
      );
    }
    return (
      <Box
        onClick={() => {
          if (currentUser?.role !== ADMIN_ROLES.JUNIOR) {
            handleCellClick(row._id, field);
          }
        }}
        sx={{
          cursor: "pointer",
          width: "100%",
          minHeight: "24px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {displayValue || "-"}
      </Box>
    );
  };

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "70px",
    },
    {
      name: "Image",
      selector: (row) => row.imagePath,
      width: "80px",
      center: true,
      cell: (row) => (
        <Avatar
          src={row.imagePath}
          alt={row.displayName || "Player"}
          sx={{
            width: 40,
            height: 40,
            bgcolor: "white",
            border: "1px solid #e0e0e0",
            "& .MuiSvgIcon-root": {
              color: "#9e9e9e",
            },
          }}
        >
          <PersonIcon />
        </Avatar>
      ),
    },
    {
      name: "Full Name",
      minWidth: "160px",
      cell: (row) => renderEditableCell(row, "fullName", "text"),
    },
    {
      name: "Display Name",
      minWidth: "160px",
      cell: (row) => renderEditableCell(row, "displayName", "text"),
    },
    {
      name: "Price",
      width: "120px",
      cell: (row) => renderEditableCell(row, "price", "number"),
    },
    {
      name: "Teams",
      width: "120px",
      center: true,
      ignoreRowClick: true,
      cell: (row) => (
        <Tooltip title="Team positions">
          <IconButton
            color="primary"
            onClick={() => handleOpenTeamPositions(row._id)}
          >
            <GroupsIcon />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      name: "Default Position",
      width: "180px",
      cell: (row) => {
        const field = "positionId";
        const isEditing =
          editingCell.id === row._id && editingCell.field === field;

        const displayValue =
          editedRows[row._id]?.[field] !== undefined
            ? editedRows[row._id][field]
            : row[field];

        const displayLabel = positionMap[displayValue]
          ? capitalize(positionMap[displayValue])
          : displayValue;

        if (isEditing) {
          return (
            <Select
              value={displayValue || ""}
              onChange={(e) => {
                handleCellChange(row._id, field, e.target.value);
                setEditingCell({ id: null, field: null });
              }}
              onBlur={() => setEditingCell({ id: null, field: null })}
              autoFocus
              defaultOpen
              variant="standard"
              fullWidth
              sx={{ fontSize: "0.875rem" }}
            >
              {Object.entries(positionMap).map(([key, value]) => (
                <MenuItem key={key} value={parseInt(key)}>
                  {capitalize(value)}
                </MenuItem>
              ))}
            </Select>
          );
        }
        return (
          <Box
            onClick={() => {
              if (currentUser?.role !== ADMIN_ROLES.JUNIOR) {
                handleCellClick(row._id, field);
              }
            }}
            sx={{
              cursor: "pointer",
              width: "100%",
              minHeight: "24px",
              display: "flex",
              alignItems: "center",
            }}
          >
            {displayLabel || "-"}
          </Box>
        );
      },
    },
  ];

  const tableData = data.map((row) => {
    if (editedRows[row._id]) {
      return { ...row, isEdited: true };
    }
    return row;
  });

  const conditionalRowStyles = [
    {
      when: (row) => row.isEdited,
      style: {
        backgroundColor: "rgba(21, 28, 36, 0.95) !important",
        color: "white !important",
        "&:hover": {
          backgroundColor: "rgba(21, 28, 36, 0.95) !important",
        },
      },
    },
  ];
  return (
    <Box className="user-management-container player-management-page">
      <Helmet>
        <title>Player Management</title>
      </Helmet>
      <Fade in timeout={500}>
        <Stack spacing={4}>
          <Paper elevation={0} className="user-header-paper">
            <Box className="user-header-content">
              <Box className="user-header-left">
                <Avatar className="user-header-avatar">
                  <SportsHandball className="user-header-icon" />
                </Avatar>
                <Box>
                  <Typography variant="h4" className="user-header-title">
                    Player Management
                  </Typography>
                  <Typography variant="body1" className="user-header-subtitle">
                    Manage and monitor all players in your system
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>

          <Paper elevation={0} className="user-controls-paper">
            <Box className="user-controls-content">
              <Box className="user-controls-left">
                <Autocomplete
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: 180 },
                    minWidth: { xs: 0, sm: 180 },
                    mr: { xs: 0, sm: 1 },
                  }}
                  options={countryOptions}
                  value={
                    countryOptions.find(
                      (o) => o.value === selectedCountry,
                    ) || null
                  }
                  onChange={handleCountryAutocompleteChange}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Country" variant="outlined" />
                  )}
                />
                <Autocomplete
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: 180 },
                    minWidth: { xs: 0, sm: 180 },
                    mr: { xs: 0, sm: 1 },
                  }}
                  options={leagueOptions}
                  value={
                    leagueOptions.find((o) => o.value === selectedLeague) ||
                    null
                  }
                  onChange={handleLeagueAutocompleteChange}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="League" variant="outlined" />
                  )}
                />
                <Autocomplete
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: 200 },
                    minWidth: { xs: 0, sm: 200 },
                    mr: { xs: 0, sm: 1 },
                  }}
                  options={teamOptions}
                  loading={teamLoading}
                  value={selectedTeamObj}
                  onChange={handleTeamChange}
                  filterOptions={(options) => options}
                  noOptionsText={
                    selectedCountry || selectedLeague
                      ? "No teams in selected country/league"
                      : "No teams found"
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      debouncedTeamSearch(
                        newInputValue,
                        selectedCountry,
                        selectedLeague,
                      );
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Team"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {teamLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
                <TableDropDown
                  filterName="Position"
                  data={Object.entries(positionMap).map(([key, value]) => ({
                    value: parseInt(key),
                    label: capitalize(value),
                  }))}
                  value={selectedPosition}
                  setValue={handlePositionChange}
                  sx={{
                    width: { xs: "100%", sm: 150 },
                    minWidth: { xs: 0, sm: 150 },
                    marginRight: { xs: 0, sm: 1 },
                  }}
                />
                <Input
                  value={searchItem}
                  placeholder="Search players..."
                  onChange={handleSearchChange}
                  className="user-search-input"
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear-search"
                        onClick={() => {
                          setSearchItem("");
                          debouncedSearch("");
                        }}
                        className="user-clear-search-button"
                      >
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </Box>

              {currentUser?.role !== ADMIN_ROLES.JUNIOR && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    width: { xs: "100%", sm: "auto" },
                    "& .MuiButton-root": { flex: { xs: 1, sm: "initial" } },
                  }}
                >
                  <Button
                    size="medium"
                    variant="contained"
                    onClick={handleSaveAll}
                    disabled={Object.keys(editedRows).length === 0}
                  >
                    Save changes
                  </Button>
                  <Button
                    size="medium"
                    variant="outlined"
                    onClick={handleReset}
                    disabled={!canReset}
                  >
                    Reset
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>

          <Paper
            elevation={0}
            className="user-content-paper"
            sx={{ position: "relative" }}
          >
            <Box>
              <DataTable
                customStyles={{
                  headCells: {
                    style: {
                      fontSize: "15px",
                      fontWeight: "bold",
                      backgroundColor: "rgba(0, 161, 221, 0.1)",
                      borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                      color: "#333",
                    },
                  },
                  rows: {
                    style: {
                      backgroundColor: "transparent",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                      },
                    },
                  },
                }}
                columns={columns}
                data={tableData}
                pagination
                paginationServer
                paginationTotalRows={totalPages}
                paginationCurrentPage={currentPage}
                paginationDefaultPage={currentPage}
                onChangePage={handlePageChange}
                paginationPerPage={pageSize}
                paginationRowsPerPageOptions={[10, 20, 50]}
                onChangeRowsPerPage={(currentRowsPerPage) =>
                  setPageSize(currentRowsPerPage)
                }
                progressPending={isLoading || !loaderDelay}
                progressComponent={<Loader />}
                className="user-datatable"
                conditionalRowStyles={conditionalRowStyles}
              />
            </Box>
            {Object.keys(editedRows).length > 0 && (
              <Fade in={true}>
                <Box
                  sx={{
                    position: { xs: "static", md: "absolute" },
                    bottom: { md: 12 },
                    left: { md: 16 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "center", md: "flex-start" },
                    padding: "4px 12px",
                    zIndex: 10,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "white", fontWeight: 600 }}
                  >
                    {Object.keys(editedRows).length} Unsaved Player
                    {Object.keys(editedRows).length !== 1 ? "s" : ""}
                  </Typography>
                </Box>
              </Fade>
            )}
          </Paper>
        </Stack>
      </Fade>

      <TeamPositionsModal
        open={teamPositionsOpen}
        playerId={teamPositionsPlayerId}
        canEdit={currentUser?.role !== ADMIN_ROLES.JUNIOR}
        onClose={handleCloseTeamPositions}
        onSaved={() => setRefresh((prev) => !prev)}
      />
    </Box>
  );
}
